import * as Sentry from '@sentry/react';
import { request } from 'actions/helper';
import { CHECKR_BFF_API_BASE } from '../../../constants';

// TODO: Handle error from ReactFilestack

// FileStack link is deleted asynchronously to allow time
// to upload actual documents to Checkr API
const deleteFileStackLink = (file: any) => {
  const options = {
    method: 'POST',
    body: JSON.stringify({ url: file.url }),
    retryDelay: 200,
  };
  return request(`${CHECKR_BFF_API_BASE}/delete_file`, options);
};

// The fields of Filestack's PickerFileMetadata the guard reads. Every field
// is optional: Filestack types `url` as always present, but production has
// delivered finished uploads without one. (filestack-js is shimmed as `any`
// in components/fields/InputFile/filestack.d.ts, so its types can't be used.)
type FinishedUpload = {
  url?: string;
  handle?: string;
  source?: string;
  status?: string;
  mimetype?: string;
  size?: number;
};

const FILESTACK_CDN = 'https://cdn.filestackcontent.com';

/**
 * Reports an upload that finished without a Filestack URL, so the upstream
 * cause stays diagnosable (INC-12060). The file is a candidate's document:
 * report only metadata that cannot identify them or their file.
 */
const reportMissingUrl = (file: FinishedUpload, uploadFlow: string): void => {
  // Sentry groups by stack trace, which shifts with the CDN-loaded picker and
  // each release's chunks, so the fingerprint pins one issue; the tags carry
  // what varies. Never add filename, originalPath, key, container or handle.
  Sentry.captureException(new Error('filestack upload finished without url'), {
    fingerprint: ['filestack-upload-finished-without-url'],
    tags: {
      uploadFlow,
      filestackSource: file.source,
      filestackStatus: file.status,
      filestackMimetype: file.mimetype,
    },
    extra: { size: file.size, hasHandle: Boolean(file.handle) },
  });
};

/**
 * deleteFileStackLink for uploads that finish without a url (INC-12060).
 * Used only where cdx_cp_filestack_missing_url_guard is on for the account;
 * see useMissingUrlGuard.
 *
 * `uploadFlow` names the caller in the Sentry report: Filestack invokes this
 * as onFileUploadFinished, so no component appears in the stack trace.
 */
export const deleteFileStackLinkGuarded = (
  file: FinishedUpload,
  uploadFlow: string,
) => {
  if (file.url) return deleteFileStackLink(file);

  reportMissingUrl(file, uploadFlow);
  // The BFF deletes by the handle in the url's path, so a handle alone is
  // enough. Without either, posting would only earn a 422.
  if (!file.handle) return Promise.resolve(undefined);
  return deleteFileStackLink({
    url: `${FILESTACK_CDN}/${encodeURIComponent(file.handle)}`,
  });
};

export default deleteFileStackLink;
